import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { MapPin, Star } from "lucide-react";
import { BACKEND_URL } from "@/config/runtime";
import { getResponsiveSrc, handleImageError } from "@/utils/responsiveImage";

const CardFace = ({ listing, isPromoted }) => {
  if (!listing) return null;
  const displayImage = listing.flyer_image || listing.gallery_images?.[0];
  const focalPoint = listing.flyer_focal_point || { x: 50, y: 50 };
  const displayMode = listing.flyer_display_mode || "focal_point";

  const getLocation = () => {
    if (
      listing.service_areas?.some((a) => a.toLowerCase() === "anywhere") ||
      (!listing.locations?.length && !listing.service_areas?.length)
    )
      return "Anywhere";
    if (listing.service_areas?.length > 1) return "Multiple";
    return listing.service_areas?.[0] || listing.locations?.[0]?.city || "Anywhere";
  };

  return (
    <>
      <div className="discover-card-face-image">
        {displayImage ? (
          <img
            src={getResponsiveSrc(displayImage)}
            onError={(e) => handleImageError(e, displayImage)}
            alt={listing.title}
            className={displayMode === "fit_to_view" ? "object-contain" : "object-cover"}
            style={
              displayMode === "fit_to_view"
                ? { backgroundColor: "#f5f5f0" }
                : { objectPosition: `${focalPoint.x}% ${focalPoint.y}%` }
            }
            loading="lazy"
            draggable={false}
            sizes="(max-width: 640px) 140px, 180px"
            width={180}
            height={180}
          />
        ) : (
          <div className="w-full h-full bg-[#2D5A3D]/40 flex items-center justify-center text-white/80 text-xs font-bold">
            {listing.title?.charAt(0) || "V"}
          </div>
        )}
      </div>
      {/* Promoted badge */}
      {isPromoted && (
        <div className="absolute top-1 left-1 z-10">
          <div className="w-4 h-4 sm:w-5 sm:h-5 rounded-full bg-amber-400/90 flex items-center justify-center shadow-sm">
            <Star className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-white fill-white" />
          </div>
        </div>
      )}
      <div className="discover-card-face-info">
        <p className="text-white text-[10px] sm:text-xs font-semibold leading-tight line-clamp-1 drop-shadow">
          {listing.title}
        </p>
        <div className="flex items-center gap-0.5 text-white/70 mt-px">
          <MapPin className="w-2 h-2 sm:w-2.5 sm:h-2.5 flex-shrink-0" />
          <span className="text-[8px] sm:text-[10px] line-clamp-1">{getLocation()}</span>
        </div>
      </div>
    </>
  );
};

export const DiscoverCarousel = () => {
  const [allListings, setAllListings] = useState([]);
  const [promotedListings, setPromotedListings] = useState([]);
  const [ready, setReady] = useState(false);
  const navigate = useNavigate();

  // Slots 0 (left) and 2 (right) use allListings, slot 1 (center) uses promotedListings
  const [sideSlots, setSideSlots] = useState([
    { frontIdx: 0, backIdx: 2, flipped: false },
    { frontIdx: 1, backIdx: 3, flipped: false },
  ]);
  const [centerSlot, setCenterSlot] = useState({ frontIdx: 0, backIdx: 1, flipped: false });

  const sideSlotsRef = useRef(sideSlots);
  const centerSlotRef = useRef(centerSlot);
  const allRef = useRef([]);
  const promoRef = useRef([]);
  const sideCountsRef = useRef({});
  const sideTimesRef = useRef({});
  const promoCountsRef = useRef({});
  const promoTimesRef = useRef({});

  useEffect(() => { sideSlotsRef.current = sideSlots; }, [sideSlots]);
  useEffect(() => { centerSlotRef.current = centerSlot; }, [centerSlot]);
  useEffect(() => { allRef.current = allListings; }, [allListings]);
  useEffect(() => { promoRef.current = promotedListings; }, [promotedListings]);

  // Fetch both pools in parallel
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [allRes, promoRes] = await Promise.all([
          fetch(`${BACKEND_URL}/api/listings/discover-carousel?batch_size=40`),
          fetch(`${BACKEND_URL}/api/listings/promoted-homepage`),
        ]);

        let allData = [];
        let promos = [];

        if (allRes.ok) {
          const data = await allRes.json();
          if (data.listings?.length >= 4) allData = data.listings;
        }
        if (promoRes.ok) {
          const data = await promoRes.json();
          promos = data.listings || [];
        }

        // Set up promoted center slot — random starting point
        if (promos.length >= 1) {
          setPromotedListings(promos);
          const pStart = Math.floor(Math.random() * promos.length);
          const pBack = promos.length > 1 ? (pStart + 1) % promos.length : pStart;
          setCenterSlot({ frontIdx: pStart, backIdx: pBack, flipped: false });
          const now = Date.now();
          const counts = {};
          const times = {};
          for (let i = 0; i < promos.length; i++) { counts[i] = 0; times[i] = 0; }
          counts[pStart] = 1; times[pStart] = now;
          if (promos.length > 1) { counts[pBack] = 1; times[pBack] = now; }
          promoCountsRef.current = counts;
          promoTimesRef.current = times;
        }

        // Set up side slots — remove promoted listings from pool entirely
        if (allData.length >= 4) {
          const promoIds = new Set(promos.map((p) => p.listing_id));
          const filtered = allData.filter((l) => !promoIds.has(l.listing_id));
          // Use filtered list as the side pool (promoted can never appear on sides)
          const sidePool = filtered.length >= 4 ? filtered : allData;
          setAllListings(sidePool);

          // Shuffle and pick 4 unique starting indices
          const indices = Array.from({ length: sidePool.length }, (_, i) => i);
          for (let i = indices.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [indices[i], indices[j]] = [indices[j], indices[i]];
          }
          const picks = indices.slice(0, 4);
          while (picks.length < 4) picks.push(picks.length % sidePool.length);
          const s0 = [
            { frontIdx: picks[0], backIdx: picks[1], flipped: false },
            { frontIdx: picks[2], backIdx: picks[3], flipped: false },
          ];
          setSideSlots(s0);
          const now = Date.now();
          const counts = {};
          const times = {};
          for (let i = 0; i < sidePool.length; i++) { counts[i] = 0; times[i] = 0; }
          s0.forEach((s) => {
            [s.frontIdx, s.backIdx].forEach((idx) => {
              counts[idx % sidePool.length] = 1;
              times[idx % sidePool.length] = now;
            });
          });
          sideCountsRef.current = counts;
          sideTimesRef.current = times;
        }

        // Mark ready only after all state is prepared
        setReady(true);
      } catch (err) {
        console.error("Failed to fetch carousel data:", err);
      }
    };
    fetchData();
  }, []);

  // Generic picker: given a pool size, occupied set, counts obj, times obj → pick best index
  const pickFrom = useCallback((poolSize, occupied, counts, times) => {
    if (!poolSize) return 0;
    const now = Date.now();
    const tier1 = [];
    const tier2 = [];
    for (let i = 0; i < poolSize; i++) {
      if (occupied.has(i)) continue;
      if (now - (times[i] || 0) >= 15000) tier1.push(i);
      else tier2.push(i);
    }
    const pool = tier1.length ? tier1 : tier2.length ? tier2 : [...Array(poolSize).keys()];
    const minCount = Math.min(...pool.map((i) => counts[i] || 0));
    const best = pool.filter((i) => (counts[i] || 0) === minCount);
    const pick = best[Math.floor(Math.random() * best.length)];
    counts[pick] = (counts[pick] || 0) + 1;
    times[pick] = now;
    return pick;
  }, []);

  // Preload an image — resolves when loaded or after timeout
  const preloadImage = useCallback((listing) => {
    const src = listing?.flyer_image || listing?.gallery_images?.[0];
    if (!src) return Promise.resolve();
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = resolve;
      img.onerror = resolve;
      img.src = src;
      // Don't wait longer than 800ms
      setTimeout(resolve, 800);
    });
  }, []);

  // Flip a side slot (0 = left, 1 = right)
  // Side pool already excludes promoted listings — no cross-pool conflicts possible
  const flipSideSlot = useCallback((idx) => {
    const all = allRef.current;
    if (all.length < 4) return;

    const occupied = new Set();
    sideSlotsRef.current.forEach((s) => {
      occupied.add(s.frontIdx % all.length);
      occupied.add(s.backIdx % all.length);
    });

    const next = pickFrom(all.length, occupied, sideCountsRef.current, sideTimesRef.current);
    const nextListing = all[next];

    setSideSlots((prev) => {
      const updated = [...prev];
      const slot = { ...updated[idx] };
      if (slot.flipped) { slot.frontIdx = next; }
      else { slot.backIdx = next; }
      updated[idx] = slot;
      return updated;
    });

    requestAnimationFrame(() => {
      preloadImage(nextListing).then(() => {
        setSideSlots((prev) => {
          const updated = [...prev];
          const slot = { ...updated[idx] };
          slot.flipped = !slot.flipped;
          updated[idx] = slot;
          return updated;
        });
      });
    });
  }, [pickFrom, preloadImage]);

  // Flip center slot (promoted)
  const flipCenter = useCallback(() => {
    const promos = promoRef.current;
    if (promos.length < 2) return;
    const occupied = new Set();
    const cs = centerSlotRef.current;
    occupied.add(cs.frontIdx % promos.length);
    occupied.add(cs.backIdx % promos.length);
    const next = pickFrom(promos.length, occupied, promoCountsRef.current, promoTimesRef.current);
    const nextListing = promos[next];

    // Prep the back face first
    setCenterSlot((prev) => {
      const slot = { ...prev };
      if (slot.flipped) { slot.frontIdx = next; }
      else { slot.backIdx = next; }
      return slot;
    });

    // Preload then flip
    requestAnimationFrame(() => {
      preloadImage(nextListing).then(() => {
        setCenterSlot((prev) => {
          const slot = { ...prev };
          slot.flipped = !slot.flipped;
          return slot;
        });
      });
    });
  }, [pickFrom, preloadImage]);

  // Regular intervals, but first flip happens as if timers started 2s before page load
  // left: 3.2s interval → first flip at 1.2s, then every 3.2s
  // center: 4.1s interval → first flip at 2.1s, then every 4.1s
  // right: 3.7s interval → first flip at 1.7s, then every 3.7s
  useEffect(() => {
    if (!ready || allListings.length < 4) return;
    const hasPromo = promotedListings.length >= 2;
    const offset = 2000;

    const schedule = [
      { fn: () => flipSideSlot(0), interval: 3200 },
      ...(hasPromo ? [{ fn: flipCenter, interval: 4100 }] : []),
      { fn: () => flipSideSlot(1), interval: 3700 },
    ];

    const timers = [];
    const intervals = [];

    schedule.forEach(({ fn, interval }) => {
      const firstDelay = interval - offset;
      const t = setTimeout(() => {
        fn();
        const id = setInterval(fn, interval);
        intervals.push(id);
      }, firstDelay);
      timers.push(t);
    });

    return () => {
      timers.forEach(clearTimeout);
      intervals.forEach(clearInterval);
    };
  }, [ready, allListings.length, promotedListings.length, flipSideSlot, flipCenter]);

  const handleCardClick = (slug) => {
    if (slug) navigate(`/listing/${slug}`);
  };

  if (!ready || allListings.length < 4) return null;

  const leftSlot = sideSlots[0];
  const rightSlot = sideSlots[1];
  const leftFront = allListings[leftSlot.frontIdx % allListings.length];
  const leftBack = allListings[leftSlot.backIdx % allListings.length];
  const rightFront = allListings[rightSlot.frontIdx % allListings.length];
  const rightBack = allListings[rightSlot.backIdx % allListings.length];

  const hasPromoted = promotedListings.length >= 1;
  const centerFront = hasPromoted ? promotedListings[centerSlot.frontIdx % promotedListings.length] : null;
  const centerBack = hasPromoted && promotedListings.length > 1
    ? promotedListings[centerSlot.backIdx % promotedListings.length]
    : centerFront;

  const renderSlot = (frontListing, backListing, flipped, posStyle, key, isPromoted) => {
    const visible = flipped ? backListing : frontListing;
    return (
      <div key={key} className="discover-slot-wrapper" style={posStyle}>
        <div
          className={`discover-flip-card ${flipped ? "flipped" : ""}`}
          onClick={() => handleCardClick(visible?.slug)}
          data-testid={`discover-card-${key}`}
        >
          <div className="discover-flip-front">
            <CardFace listing={frontListing} isPromoted={isPromoted} />
          </div>
          <div className="discover-flip-back">
            <CardFace listing={backListing} isPromoted={isPromoted} />
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="discover-hero-carousel" data-testid="discover-carousel-section">
      <div className="discover-hero-track">
        {renderSlot(leftFront, leftBack, leftSlot.flipped,
          { transform: "translateX(-12%) rotateY(8deg) scale(0.88)", zIndex: 2, opacity: 0.75 },
          "left", false)}
        {hasPromoted && renderSlot(centerFront, centerBack, centerSlot.flipped,
          { transform: "rotateY(0deg) scale(1.08)", zIndex: 3, opacity: 1 },
          "center", true)}
        {renderSlot(rightFront, rightBack, rightSlot.flipped,
          { transform: "translateX(12%) rotateY(-8deg) scale(0.88)", zIndex: 2, opacity: 0.75 },
          "right", false)}
      </div>
    </div>
  );
};
